import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BellRing, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Switch } from "../ui/switch";

const TIERS = [60, 30, 14, 7, 1, 0];

export default function DocumentReminderSection() {
  const { t } = useTranslation("common");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const r = await api.get("/reminder-preferences/config");
      setConfig(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/reminder-preferences/config", patch);
      setConfig(r.data);
      toast.success(t("settings.document_reminders.saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const toggleTier = (tier) => {
    const current = config?.tiers || [];
    const next = current.includes(tier)
      ? current.filter((t_) => t_ !== tier)
      : [...current, tier];
    saveConfig({ tiers: next });
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-6 flex items-center justify-center text-zinc-400">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("common.loading")}
      </div>
    );
  }

  return (
    <section
      className="rounded-2xl border border-zinc-200 bg-white p-6 mt-6"
      data-testid="document-reminder-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-600 inline-flex items-center gap-1.5">
            <BellRing size={12} strokeWidth={1.75} />
            QENVA · {t("settings.document_reminders.title")}
          </p>
          <h2 className="font-display text-2xl font-semibold text-qenva-navy mt-1">
            {t("settings.document_reminders.title")}
          </h2>
          <p className="text-sm text-zinc-500 mt-1.5 max-w-xl">
            {t("settings.document_reminders.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12px] text-zinc-500 font-medium">
            {config.enabled
              ? t("settings.document_reminders.enabled")
              : t("settings.document_reminders.disabled")}
          </span>
          <Switch
            checked={!!config.enabled}
            disabled={saving}
            onCheckedChange={(v) => saveConfig({ enabled: !!v })}
            data-testid="document-reminders-enabled-toggle"
          />
        </div>
      </div>

      <div className={`mt-6 ${config.enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-2">
          {t("settings.document_reminders.tiers_label")}
        </p>
        <div className="flex flex-wrap gap-2">
          {TIERS.map((tier) => {
            const active = (config.tiers || []).includes(tier);
            return (
              <button
                key={tier}
                type="button"
                onClick={() => toggleTier(tier)}
                disabled={saving}
                data-testid={`document-reminder-tier-${tier}`}
                className={`px-3 py-1.5 text-[12px] font-semibold rounded-full border transition-colors ${
                  active
                    ? "bg-qenva-navy text-white border-qenva-navy"
                    : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"
                }`}
              >
                {t(`settings.document_reminders.tier_${tier}`)}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
